// ClassificationsPanel - Classification results table with filters for Email Monitor
// Extracted from EmailClassificationPage for the unified Email Monitor page.

import { useState, useEffect, useCallback } from 'react';
import { emailMonitorDecisionService } from '../../services/emailMonitorDecisions';
import type {
  EmailClassificationResult,
  EmailClassificationStats,
  GetClassificationParams,
  EmailCategory,
} from '../../services/emailMonitorDecisions';
import ClassificationDetailModal from './ClassificationDetailModal';
import ConfidenceBadge from '../portal/ConfidenceBadge';
import { formatDate } from '../../utils/dateFormatters';
import styles from './ClassificationsPanel.module.css';

// --------------------------------------------------------------------------
// Helper components
// --------------------------------------------------------------------------

const CATEGORY_COLORS: Record<string, string> = {
  order: '#27ae60',
  quote: '#094290',
  inquiry: '#8e44ad',
  support: '#e67e22',
  payment: '#16a085',
  shipping: '#d35400',
  internal: '#7f8c8d',
  spam: '#c0392b',
  other: '#95a5a6',
};

function CategoryBadge({ category, isHumanVerified }: { category: string; isHumanVerified?: boolean }) {
  const bg = CATEGORY_COLORS[category] || '#95a5a6';
  return (
    <span
      className={styles.categoryBadge}
      style={{ background: bg }}
      title={isHumanVerified ? 'Human verified' : 'LLM classified'}
    >
      {category}
      {isHumanVerified && ' \u2713'}
    </span>
  );
}

function ConfidenceBar({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const color = pct >= 90 ? '#27ae60' : pct >= 70 ? '#f39c12' : '#EE3C33';
  return (
    <span className={styles.confidenceBar} title={`${pct}%`}>
      <span className={styles.confidenceTrack}>
        <span className={styles.confidenceFill} style={{ width: `${pct}%`, background: color }} />
      </span>
      <span className={styles.confidenceLabel}>{pct}%</span>
    </span>
  );
}

function SenderMatchBadge({ matched, method }: { matched: boolean; method: string | null }) {
  if (!matched) return <span className={styles.senderNoMatch}>No match</span>;
  return (
    <span className={styles.senderMatched} title={method || ''}>
      {method === 'exact_email' ? 'Exact' : method === 'domain_match' ? 'Domain' : method || 'Yes'}
    </span>
  );
}

function CsrBadge({ name, assignmentType, confidence }: { name: string | null; assignmentType: string | null; confidence: number | null }) {
  if (!name) return <span className={styles.senderNoMatch}>-</span>;
  const pct = confidence !== null ? Math.round(confidence * 100) : null;
  return (
    <span
      className={styles.csrSuggested}
      title={`${assignmentType}: ${pct !== null ? pct + '% confidence' : 'N/A'}`}
    >
      {name}{pct !== null ? ` ${pct}%` : ''}
    </span>
  );
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

// --------------------------------------------------------------------------
// Main component
// --------------------------------------------------------------------------

export default function ClassificationsPanel() {
  // Data
  const [results, setResults] = useState<EmailClassificationResult[]>([]);
  const [stats, setStats] = useState<EmailClassificationStats | null>(null);
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [humanVerifiedFilter, setHumanVerifiedFilter] = useState('');
  const [senderMatchedFilter, setSenderMatchedFilter] = useState('');
  const [minConfidenceFilter, setMinConfidenceFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Sorting
  const [sortBy, setSortBy] = useState<GetClassificationParams['sort_by']>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Detail modal
  const [selectedResult, setSelectedResult] = useState<EmailClassificationResult | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch results
  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params: GetClassificationParams = {
        offset: page * pageSize,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (categoryFilter) params.category = categoryFilter;
      if (humanVerifiedFilter) params.human_verified = humanVerifiedFilter === 'true';
      if (senderMatchedFilter) params.sender_matched = senderMatchedFilter === 'true';
      if (minConfidenceFilter) params.min_confidence = parseFloat(minConfidenceFilter);
      if (searchQuery) params.search = searchQuery;

      const response = await emailMonitorDecisionService.getClassificationResults(params);
      setResults(response.items);
      setTotal(response.total);
    } catch {
      // Silent fail - panel still renders
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortOrder, categoryFilter, humanVerifiedFilter, senderMatchedFilter, minConfidenceFilter, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await emailMonitorDecisionService.getClassificationStats();
      setStats(data);
    } catch {
      // silent
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await emailMonitorDecisionService.getCategories();
      setCategories(data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchResults();
    fetchStats();
    fetchCategories();
  }, [fetchResults, fetchStats, fetchCategories]);

  // Open detail modal
  const openDetail = async (resultId: string) => {
    setDetailLoading(true);
    try {
      const detail = await emailMonitorDecisionService.getClassificationResult(resultId);
      setSelectedResult(detail);
    } catch {
      // silent
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSort = (field: GetClassificationParams['sort_by']) => {
    if (sortBy === field) {
      setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className={styles.panel}>
      {/* Stats summary */}
      {stats && (
        <div className={styles.statsRow}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.total_classified}</span>
            <span className={styles.statLabel}>Classified</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.total_unclassified}</span>
            <span className={styles.statLabel}>Unclassified</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.human_verified_count}</span>
            <span className={styles.statLabel}>Verified</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>
              {stats.accuracy !== null ? `${Math.round(stats.accuracy)}%` : '-'}
            </span>
            <span className={styles.statLabel}>Accuracy</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>
              {Math.round(stats.avg_confidence * 100)}%
            </span>
            <span className={styles.statLabel}>Avg Confidence</span>
          </div>
          {/* Category breakdown */}
          {Object.entries(stats.by_category).length > 0 && (
            <div className={styles.categoryBreakdown}>
              {Object.entries(stats.by_category).map(([cat, count]) => (
                <span
                  key={cat}
                  className={styles.catStat}
                  style={{ borderColor: CATEGORY_COLORS[cat] || '#95a5a6' }}
                >
                  {cat}: {count}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className={styles.filterRow}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search subject, sender..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setPage(0); fetchResults(); } }}
        />
        <select
          className={styles.filterSelect}
          value={categoryFilter}
          onChange={e => { setCategoryFilter(e.target.value); setPage(0); }}
        >
          <option value="">All Categories</option>
          {categories.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select
          className={styles.filterSelect}
          value={humanVerifiedFilter}
          onChange={e => { setHumanVerifiedFilter(e.target.value); setPage(0); }}
        >
          <option value="">All Verified</option>
          <option value="true">Verified</option>
          <option value="false">Not Verified</option>
        </select>
        <select
          className={styles.filterSelect}
          value={senderMatchedFilter}
          onChange={e => { setSenderMatchedFilter(e.target.value); setPage(0); }}
        >
          <option value="">All Senders</option>
          <option value="true">Matched</option>
          <option value="false">Not Matched</option>
        </select>
        <select
          className={styles.filterSelect}
          value={minConfidenceFilter}
          onChange={e => { setMinConfidenceFilter(e.target.value); setPage(0); }}
        >
          <option value="">Any Confidence</option>
          <option value="0.9">90%+</option>
          <option value="0.8">80%+</option>
          <option value="0.5">50%+</option>
          <option value="0.3">30%+ (Low)</option>
        </select>
        <span className={styles.totalCount}>{total} results</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className={styles.loading}>Loading classifications...</div>
      ) : results.length === 0 ? (
        <div className={styles.empty}>No classification results found.</div>
      ) : (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.sortable} onClick={() => handleSort('created_at')}>
                  Date {sortBy === 'created_at' && (sortOrder === 'desc' ? '\u2193' : '\u2191')}
                </th>
                <th>From</th>
                <th>Subject</th>
                <th className={styles.sortable} onClick={() => handleSort('llm_category')}>
                  Category {sortBy === 'llm_category' && (sortOrder === 'desc' ? '\u2193' : '\u2191')}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('llm_confidence')}>
                  Confidence {sortBy === 'llm_confidence' && (sortOrder === 'desc' ? '\u2193' : '\u2191')}
                </th>
                <th>Sender</th>
                <th>Customer</th>
                <th>CSR</th>
              </tr>
            </thead>
            <tbody>
              {results.map(r => (
                <tr key={r.id} className={styles.row} onClick={() => openDetail(r.id)}>
                  <td className={styles.dateCell}>
                    {formatRelativeTime(r.email_received_at || r.created_at)}
                  </td>
                  <td className={styles.fromCell} title={r.email_from || ''}>
                    {r.email_from || '-'}
                  </td>
                  <td className={styles.subjectCell} title={r.email_subject || ''}>
                    {r.email_subject || '-'}
                  </td>
                  <td>
                    <CategoryBadge category={r.effective_category} isHumanVerified={r.human_verified} />
                  </td>
                  <td>
                    {r.confidence_bucket ? (
                      <ConfidenceBadge bucket={r.confidence_bucket} />
                    ) : (
                      <ConfidenceBar score={r.llm_confidence} />
                    )}
                  </td>
                  <td>
                    <SenderMatchBadge matched={r.sender_matched} method={r.sender_match_method} />
                  </td>
                  <td className={styles.customerCell}>
                    {r.matched_customer_name || '-'}
                  </td>
                  <td>
                    <CsrBadge name={r.csr_name} assignmentType={r.csr_assignment_type} confidence={r.csr_confidence} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
                Previous
              </button>
              <span>Page {page + 1} of {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {(selectedResult || detailLoading) && (
        <ClassificationDetailModal
          result={selectedResult}
          loading={detailLoading}
          categories={categories.map(c => c.value as EmailCategory)}
          onClose={() => setSelectedResult(null)}
          onVerified={(updated) => {
            setSelectedResult(updated);
            fetchStats();
            fetchResults();
          }}
        />
      )}
    </div>
  );
}
